import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ReturnViewModel } from '../interfaces/viewmodels/returnViewModel';
import { ActiveProblem, BitSummary, Field_MudType, Order_Base, Productline, RtaType } from '../interfaces/request';
import { ORD_Order } from '../interfaces/request.interface';
import { DashboardAnalyticsDto, DashboardFilterOptions, FieldRequest, OrderDashboardItem, PagedResult, PriorityOption, ReviewQueueItemDto, UpdateOrderPriorityRequest } from '../interfaces/rta';

declare var RTA_API: string;
declare var ADESC_API: string;
declare var COMMON_API: string;
declare var FILEVAULT_URL: string;
declare var CURRENT_USER_ID: number;
declare var CURRENT_USER_LOGINID: string;
declare var CURRENT_USER_DISPLAYNAME: string;

const isDate: Function = (value: any): boolean => value && value.getTime;
const toUTC = (date: Date) =>
  new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  ));

@Injectable({
  providedIn: 'root'
})

export class RtaService {
  public $CURRENT_USER_LOGINID: string = CURRENT_USER_LOGINID;
  public $CURRENT_USER_DISPLAYNAME: string = CURRENT_USER_DISPLAYNAME;

  RtaApi: string = RTA_API;
  AdescApi: string = ADESC_API;
  CommonApi: string = COMMON_API;
  FileVaultApi: string = FILEVAULT_URL;
  CurrentUserId: number = CURRENT_USER_ID;
  constructor(public http: HttpClient) { }

  public deleteRequest(id: number): Observable<void> {
    return this.http.delete<void>(`${this.RtaApi}/Orders(${id})`)
  }
  public getRequests(): Observable<ORD_Order[]> {
    return this.http.get<ORD_Order[]>(`${this.RtaApi}/Orders`)
  }
  public getOrders(options: {
    skip: number;
    take: number;
    sortField?: string;
    sortDir?: string;
    search?: string;
    status?: string[];
    productLine?: string[];
    type?: string | null;
    series?: string[];
    assignedTo?: string | null;
    userFilter?: string | null;
    region?: string | null;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    filterId?: string | null;
    filterIdOp?: string | null;
    filterDescription?: string | null;
    filterDescriptionOp?: string | null;
    filterSize?: string | null;
    filterSizeOp?: string | null;
    filterType?: string | null;
    filterTypeOp?: string | null;
    filterLevel?: string | null;
    filterLevelOp?: string | null;
    filterReferenceNumber?: string | null;
    filterReferenceNumberOp?: string | null;
    filterMaterialNumber?: string | null;
    filterMaterialNumberOp?: string | null;
    filterServicesNumber?: string | null;
    filterServicesNumberOp?: string | null;
    filterSubmittedBy?: string | null;
    filterSubmittedByOp?: string | null;
    filterIsComplete?: boolean | null;
  }): Observable<PagedResult<OrderDashboardItem>> {
    let params = new HttpParams()
      .set('skip', options.skip)
      .set('take', options.take);

    if (options.sortField) {
      params = params.set('sortField', options.sortField);
    }
    if (options.sortDir) {
      params = params.set('sortDir', options.sortDir);
    }
    if (options.search) {
      params = params.set('search', options.search);
    }
    if (options.status && options.status.length > 0) {
      params = params.set('status', options.status.join(','));
    }
    if (options.productLine && options.productLine.length > 0) {
      params = params.set('productLine', options.productLine.join(','));
    }
    if (options.type) {
      params = params.set('type', options.type);
    }
    if (options.series && options.series.length > 0) {
      params = params.set('series', options.series.join(','));
    }
    if (options.assignedTo) {
      params = params.set('assignedTo', options.assignedTo);
    }
    if (options.userFilter) {
      params = params.set('userFilter', options.userFilter);
    }
    if (options.region) {
      params = params.set('region', options.region);
    }
    if (options.dateFrom) {
      params = params.set('dateFrom', options.dateFrom.toISOString());
    }
    if (options.dateTo) {
      params = params.set('dateTo', options.dateTo.toISOString());
    }
    if (options.filterId) {
      params = params.set('filterId', options.filterId);
      if (options.filterIdOp) params = params.set('filterIdOp', options.filterIdOp);
    }
    if (options.filterDescription) {
      params = params.set('filterDescription', options.filterDescription);
      if (options.filterDescriptionOp) params = params.set('filterDescriptionOp', options.filterDescriptionOp);
    }
    if (options.filterSize) {
      params = params.set('filterSize', options.filterSize);
      if (options.filterSizeOp) params = params.set('filterSizeOp', options.filterSizeOp);
    }
    if (options.filterType) {
      params = params.set('filterType', options.filterType);
      if (options.filterTypeOp) params = params.set('filterTypeOp', options.filterTypeOp);
    }
    if (options.filterLevel) {
      params = params.set('filterLevel', options.filterLevel);
      if (options.filterLevelOp) params = params.set('filterLevelOp', options.filterLevelOp);
    }
    if (options.filterReferenceNumber) {
      params = params.set('filterReferenceNumber', options.filterReferenceNumber);
      if (options.filterReferenceNumberOp) params = params.set('filterReferenceNumberOp', options.filterReferenceNumberOp);
    }
    if (options.filterMaterialNumber) {
      params = params.set('filterMaterialNumber', options.filterMaterialNumber);
      if (options.filterMaterialNumberOp) params = params.set('filterMaterialNumberOp', options.filterMaterialNumberOp);
    }
    if (options.filterServicesNumber) {
      params = params.set('filterServicesNumber', options.filterServicesNumber);
      if (options.filterServicesNumberOp) params = params.set('filterServicesNumberOp', options.filterServicesNumberOp);
    }
    if (options.filterSubmittedBy) {
      params = params.set('filterSubmittedBy', options.filterSubmittedBy);
      if (options.filterSubmittedByOp) params = params.set('filterSubmittedByOp', options.filterSubmittedByOp);
    }
    if (options.filterIsComplete !== undefined && options.filterIsComplete !== null) {
      params = params.set('filterIsComplete', options.filterIsComplete.toString());
    }

    return this.http.get<PagedResult<OrderDashboardItem>>(`${this.RtaApi}/GetOrders`, { params });
  }
  public getPriorities(): Observable<PriorityOption[]> {
    return this.http.get<PriorityOption[]>(`${this.RtaApi}/Priorities`);
  }
  public getDashboardFilterOptions(): Observable<DashboardFilterOptions> {
    return this.http.get<DashboardFilterOptions>(`${this.RtaApi}/GetDashboardFilterOptions`);
  }
  public getReviewQueue(): Observable<ReviewQueueItemDto[]> {
    return this.http.get<ReviewQueueItemDto[]>(`${this.RtaApi}/GetReviewQueue?userId=${this.CurrentUserId}`);
  }
  public updateOrderPriority(orderId: number, payload: UpdateOrderPriorityRequest): Observable<ReturnViewModel> {
    return this.http.post<ReturnViewModel>(`${this.RtaApi}/UpdateOrderPriority/${orderId}`, payload);
  }
  public getSubApplicationByUserId(id: number): Observable<ReturnViewModel> {
    return this.http.get<ReturnViewModel>(`${this.AdescApi}/GetAssignedSubApplications(${id})`);
  }
  public getRtaTypes(): Observable<RtaType[]> {
    return this.http.get<ReturnViewModel>(`${this.AdescApi}/Types?$filter=Rank ne 99`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getProductlines(): Observable<Productline[]> {
    return this.http.get<ReturnViewModel>(`${this.AdescApi}/ProductLines?$expand=SubPsl1`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getExistingBuilds(id: string): Observable<BitSummary> {
    return this.http.get<ReturnViewModel>(`${this.CommonApi}/BitSummaries(${id})`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getActiveProblems(): Observable<ActiveProblem[]> {
    return this.http.get<ReturnViewModel>(`${this.RtaApi}/ActiveProblems`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getMudTypes(): Observable<Field_MudType[]> {
    return this.http.get<ReturnViewModel>(`${this.AdescApi}/Field_MudType$orderby=Name`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getSubApplicationLabels(id: string): Observable<Field_MudType[]> {
    return this.http.get<ReturnViewModel>(`${this.AdescApi}/Field_MudType$orderby=Name`).pipe(
      map(res => res.info),
      catchError(error => {
        return throwError(() => new Error(error));
      }));
  }
  public getFieldRequests(key: number): Observable<FieldRequest[]> {
    return this.http.get<FieldRequest[]>(`${this.RtaApi}/GetFieldRequests/${key}`).pipe(map(x=>x),
    catchError(error => {
      return throwError(() => new Error(error));
    }));
  }
  public getFieldRequestRequirement(key: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.RtaApi}/GetFieldRequestRequirement/${key}`).pipe(map(x=>x),
    catchError(error => {
      return throwError(() => new Error(error));
    }));
  }

  public submitOrder(orderId: number): Observable<ReturnViewModel> {
    return this.http.post<ReturnViewModel>(`${this.RtaApi}/SubmitOrder/${orderId}?userId=${CURRENT_USER_ID}`, {}).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      })
    );
  }

  /**
   * Gets all enabled manufacturing facility plants
   * Backend filters by ManufactureFacility = 1 AND Enabled = 1
   */
  public getManufacturingPlants(): Observable<ReturnViewModel> {
    return this.http.get<ReturnViewModel>(`${this.RtaApi}/GetManufacturingPlants`).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      })
    );
  }

  /**
   * Import reference data from a SAP Material Number into an existing order.
   * Calls sp_BuildInitialOrderBitData to populate bit data from the reference,
   * then returns the refreshed order.
   */
  public importReferenceData(orderId: number, sapMaterialNumber: number): Observable<ReturnViewModel> {
    return this.http.post<ReturnViewModel>(
      `${this.RtaApi}/ImportReferenceData/${orderId}?userId=${CURRENT_USER_ID}&sapMaterialNumber=${sapMaterialNumber}`, {}
    ).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      })
    );
  }

  public getDashboardAnalytics(timeRange: string): Observable<DashboardAnalyticsDto> {
    let params = new HttpParams()
      .set('userId', CURRENT_USER_ID.toString())
      .set('timeRange', timeRange);

    return this.http.get<DashboardAnalyticsDto>(`${this.RtaApi}/GetDashboardAnalytics`, { params }).pipe(
      catchError(error => {
        return throwError(() => new Error(error));
      })
    );
  }
}
